// src/contexts/SupaContext.jsx

import React, { createContext, useContext, useState } from "react";
import { useAuth } from './AuthContext'; // Importar el hook useAuth para consumir AuthContext
import supabase from "../SupaCredentials"; // Importa tu cliente Supabase

// Crear el contexto SupaContext
export const SupaContext = createContext(null);

// Hook para consumir SupaContext
export const useSupaContext = () => {
    const context = useContext(SupaContext);
    if (!context) {
        throw new Error('useSupaContext debe ser utilizado dentro de un SupaProvider');
    }
    return context;
};

// Proveedor del contexto SupaContext
export const SupaProvider = ({ children }) => {
  // Obtener el estado y métodos de autenticación desde AuthContext (que usa useAuthManager)
  const auth = useAuth();

  // Estados específicos de SupaContext si son necesarios (puedes quitarlos si no los usas para otras cosas)
  const [supaLoading, setSupaLoading] = useState(false);
  const [supaError, setSupaError] = useState(null);

  // *** FUNCIONES ESPECÍFICAS DE SUPABASE (Ejemplos) ***
  // Si tienes funciones que interactúan directamente con Supabase (aparte de Auth), irían aquí.
  // Asegúrate de que manejen sus propios estados de carga/error si es necesario
  // o que actualicen supaLoading/supaError.

  const createOrder = async (/* ...datos de la orden... */) => {
    setSupaLoading(true);
    setSupaError(null);
    console.log("Función createOrder llamada (implementación pendiente)");
    try {
      // Lógica para crear orden en Supabase...
      // const { data, error } = await supabase.from('orders').insert([ ... ]);
      // if (error) throw error;
      await new Promise(resolve => setTimeout(resolve, 500)); // Simular async
      // Retornar éxito o datos
    } catch (error) {
      console.error("Error creando orden:", error);
      setSupaError("No se pudo crear la orden.");
      // Retornar fallo
    } finally {
      setSupaLoading(false);
    }
  };

  const createOrderItem = async (/* ...datos del item... */) => {
     setSupaLoading(true);
     setSupaError(null);
     console.log("Función createOrderItem llamada (implementación pendiente)");
     try {
       // Lógica para crear item...
       await new Promise(resolve => setTimeout(resolve, 500));
     } catch (error) {
       setSupaError("No se pudo añadir el ítem.");
     } finally {
       setSupaLoading(false);
     }
   };

  // ... otras funciones como createMultipleOrderItems, createOrderItemsWithSugs, etc. ...

  // Función para añadir un pre-registro (ejemplo de uso directo de supabase)
  const addPreRegistration = async (preRegData) => {
    if (!preRegData || !preRegData.email) {
      console.error("Datos insuficientes para pre-registro.");
      return null;
    }
    setSupaLoading(true);
    setSupaError(null);
    try {
      const { data, error } = await supabase
        .from('pre_registrations') // Asegúrate que la tabla exista
        .insert([preRegData])
        .select() // Para obtener el registro insertado
        .single(); // Asume que inserta uno solo

      if (error) {
        // Manejar error específico, por ejemplo, si el email ya existe (unique constraint)
        if (error.code === '23505') { // Código de error para violación de unicidad en PostgreSQL
          console.warn(`Pre-registro para ${preRegData.email} ya existe.`);
          // Opcional: podrías intentar buscar el registro existente
          const { data: existingData, error: findError } = await supabase
            .from('pre_registrations')
            .select('*')
            .eq('email', preRegData.email)
            .single();
          if (!findError && existingData) return existingData; // Devolver el existente
          // Si no se encuentra o hay otro error, lanzar el original
        }
        throw error;
      }
      console.log('Pre-registro creado/encontrado:', data);
      return data;
    } catch (err) {
      console.error("Error en addPreRegistration:", err);
      setSupaError("No se pudo completar el pre-registro.");
      return null;
    } finally {
      setSupaLoading(false);
    }
  };

    // Función para añadir una dirección (ejemplo de uso directo de supabase)
  const createAddressAfterCreateUser = async (addressData) => {
    if (!addressData || !addressData.user_id) {
      console.error("Datos de dirección o user_id faltantes.");
      return null;
    }
    setSupaLoading(true); // Podrías tener un loading específico para direcciones
    setSupaError(null);
    try {
      const { data, error } = await supabase
        .from('addresses') // Asegúrate que la tabla exista
        .insert([addressData])
        .select()
        .single();

      if (error) throw error;

      console.log('Dirección creada:', data);
      return data;
    } catch (err) {
      console.error("Error creando dirección:", err);
      setSupaError("No se pudo guardar la dirección.");
      return null;
    } finally {
      setSupaLoading(false);
    }
  };


  // Combinar el estado/métodos de AuthContext con los específicos de SupaContext
  const contextValue = {
    // *** Autenticación (viene de useAuth/AuthContext) ***
    user: auth.user,            // Objeto combinado { ...userAuth, userData }
    isActiveUser: auth.isActiveUser,
    authInitialized: auth.initialized, // Pasar el estado 'initialized' correctamente
    session: auth.session,
    login: auth.login,
    logout: auth.logout,
    registerUser: auth.registerUser,
    fetchUserData: auth.fetchUserData, // Exponer la función si es necesario

    // Estado combinado de carga (usa el loading combinado de useAuthManager)
    // Este 'loading' reflejará tanto la carga de Auth como la de UserData
    loading: auth.loading,

    // Estado combinado de error (prioriza el error de Auth, luego el de SupaContext)
    error: auth.error || supaError,

    // Limpiar errores (limpia ambos contextos)
    clearError: () => {
      auth.clearError(); // Llama al clearError de useAuthManager
      setSupaError(null);   // Limpia el error local de SupaContext
    },

    // *** Funciones específicas de SupaContext ***
    createOrder,
    createOrderItem,
    addPreRegistration,           // Función añadida
    createAddressAfterCreateUser, // Función añadida
    // ... otras funciones ...

    // Puedes exponer supaLoading y supaError si necesitas diferenciar
    // la carga/error de las operaciones específicas de SupaContext
    supaLoading,
    supaError,
  };

  // Proveer el valor combinado al contexto
  return (
    <SupaContext.Provider value={contextValue}>
        {children}
    </SupaContext.Provider>
  );
};